/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  BuilderUserContext,
  FormRootSchema,
  FormStateSchema,
  isJobEditable,
} from '@gh/builder-shared';
import { BuilderPermission, JobContractDaysPeriodType, JOB_NOT_PROCEEDING_REASON_OTHER } from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  FormState, IS_REGION_NZ, NumberTypes,
  whenAllOf,
  whenEditMode,
  whenNzRegion,
  whenOneOf,
} from '@gh/common-shared';
import { EntityVersion, ID } from '@gh/common-shared-data';
import {
  controlValues,
  FormMode,
  getControl,
  getControlValue,
  markControlAsDirty,
  setControlValue,
} from '@gh/core-ui';
import { currentDate, expandObservable, isDateValid, isNotNil } from '@gh/core-util';
import { filter$, map$ } from '@gh/rx/operators';
import { isNil, negate } from 'lodash';
import { Observable } from 'rxjs';
import { JobBasicInfo, UserListItem } from '../../../resources/job-details-tab';
import {
  ContracturalCompletionRequest,
  ContracturalCompletionResponse,
  JobDatesTabInfo,
} from '../../../resources/job-details-tab/job-details-tab-model';
import {
  isJobNotProceedingReasonRequired,
  maxLengthNotProceedingReasonOther,
  requiredNotProceedingReason,
  requiredNotProceedingReasonOther,
} from './job-not-proceeding.policy';

@Injectable()
export class JobDetailsDatesTabFormState extends FormState<JobDatesTabInfo> {
  private originalHandoverDate?: Date;
  private originalAuthToCommenceDate?: Date;
  private originalIsNotProceeding = false;
  private originalNotProceedingReason?: string | number;
  private basicInfo: JobBasicInfo;

  constructor(private schemaFactory: BuilderFormSchemaFactory,
              private userContext: BuilderUserContext) {
    super(schemaFactory);
  }

  get version(): EntityVersion {
    return getControlValue(this.group, 'version');
  }

  get supervisorId(): Maybe<ID> {
    return getControlValue(this.group, 'supervisorId');
  }

  get authToCommenceDate(): Maybe<Date> {
    return getControlValue(this.group, 'authToCommenceDate');
  }

  get isAuthToCommence(): boolean {
    return getControlValue(this.group, 'isAuthToCommence');
  }

  get sundaysAndSaturdays(): number {
    return getControlValue(this.group, 'sundaysAndSaturdays');
  }

  get nonWorkingDays(): number {
    return getControlValue(this.group, 'nonWorkingDays');
  }

  get isDisplayWorkingNonWorkingDays(): boolean {
    return isNotNil(getControlValue(this.group, 'contracturalCompletionDate'))
      && getControlValue(this.group, 'constructionPeriodIncludes') === JobContractDaysPeriodType.AllDays;
  }

  get isSupervisorExist(): boolean {
    return getControlValue(this.group, 'isSupervisorExist');
  }

  get contracturalCompletionRequest$(): Observable<ContracturalCompletionRequest> {
    return expandObservable({
      contractStartDate: controlValues(getControl(this.group, 'contractStartDate'))
        .pipe(filter$((date) => isNil(date) || isDateValid(date))),
      constructionDays: controlValues(getControl(this.group, 'constructionDays'))
        .pipe(map$((days) => days || 0)),
      timeExtensionLetterDays: controlValues(getControl(this.group, 'timeExtensionLetterDays'))
        .pipe(map$((days) => days || 0)),
    });
  }

  get isJobEditable(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT) && isJobEditable(this.basicInfo);
  }

  buildSchema(info: JobDatesTabInfo, { root }: FormStateSchema): FormRootSchema {
    const isNotNotProceeding = !info.isNotProceeding;
    const isNotNotProceedingOrFinalized = !(info.isNotProceeding || info.isFinalized);
    const isNotProceedingReasonOther = info.isNotProceeding
      && `${info.notProceedingReason}` === JOB_NOT_PROCEEDING_REASON_OTHER;
    const isNotProceedingReasonRequired = isJobNotProceedingReasonRequired(
      info.isNotProceeding, this.originalIsNotProceeding, this.originalNotProceedingReason);

    const mode = this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
      // some fields should be editable in NZ region even for finalized job
      && (IS_REGION_NZ && info.jobUseCodeOfComplience || !this.basicInfo.isFinalized) ?
      FormMode.EditMode
      : FormMode.ViewMode;

    return root({
      mode,
      enabled: whenEditMode(),
      optimisticLock: true,
      children: {
        depositDate: { enabled: false, type: DateTimeTypes.ShortDate },
        specification: { enabled: false },
        csDate: { enabled: false, type: DateTimeTypes.ShortDate },
        csSignedByClientDate: { enabled: false, type: DateTimeTypes.ShortDate },
        csSignedByOfficeDate: { enabled: false, type: DateTimeTypes.ShortDate },
        financeApprovedDate: {
          enabled: isNotNotProceedingOrFinalized && info.isFinanceApproved,
          type: DateTimeTypes.ShortDate,
        },
        isFinanceApproved: { enabled: isNotNotProceedingOrFinalized },
        contractDate: { enabled: isNotNotProceedingOrFinalized && info.isContract, type: DateTimeTypes.ShortDate },
        isContract: { enabled: isNotNotProceedingOrFinalized },
        saleDate: { enabled: false, type: DateTimeTypes.ShortDate },
        councilApprovedDate: {
          enabled: isNotNotProceedingOrFinalized && info.isCouncilApproved,
          type: DateTimeTypes.ShortDate,
        },
        isCouncilApproved: { enabled: isNotNotProceedingOrFinalized },
        councilPermitNumber: { enabled: isNotNotProceedingOrFinalized && info.isCouncilApproved },
        authToCommenceDate: {
          enabled: isNotNotProceedingOrFinalized && info.isAuthToCommence,
          type: DateTimeTypes.ShortDate,
        },
        isAuthToCommence: { enabled: isNotNotProceedingOrFinalized },
        cocIssueDate: {
          visible: whenAllOf(whenNzRegion(), info.jobUseCodeOfComplience),
          enabled: isNotNotProceeding && info.isCocComplianceIssued && isNotNil(info.handoverDate),
          type: DateTimeTypes.ShortDate,
        },
        isCocComplianceIssued: {
          visible: whenAllOf(whenNzRegion(), info.jobUseCodeOfComplience),
          enabled: isNotNotProceeding && isNotNil(info.handoverDate),
        },
        isCocGjgIssue: {
          visible: whenAllOf(whenNzRegion(), info.jobUseCodeOfComplience),
          enabled: isNotNotProceeding && (!info.isCocComplianceIssued && isNil(info.cocIssueDate))
            && isNotNil(info.handoverDate),
        },
        isCocClientIssue: {
          visible: whenAllOf(whenNzRegion(), info.jobUseCodeOfComplience),
          enabled: isNotNotProceeding && (!info.isCocComplianceIssued && isNil(info.cocIssueDate))
            && isNotNil(info.handoverDate),
        },
        contractStartDate: {
          required: info.isContractStartDateOverridden,
          enabled: isNotNotProceedingOrFinalized && info.isContractStartDateOverridden,
          type: DateTimeTypes.ShortDate,
        },
        scheduleDrawsId: { enabled: whenEditMode() },
        scheduleSummaryId: { enabled: whenEditMode() },
        isContractStartDateOverridden: { enabled: isNotNotProceedingOrFinalized },
        initialClaimDate: { enabled: false, type: DateTimeTypes.ShortDate },
        varsDays: { enabled: false },
        sundaysAndSaturdays: void 0,
        nonWorkingDays: void 0,
        constructionDays: { enabled: isNotNotProceedingOrFinalized },
        timeExtensionLetterDays: { enabled: isNotNotProceedingOrFinalized, type: NumberTypes.Days },
        contracturalCompletionDate: { enabled: isNotNotProceedingOrFinalized, type: DateTimeTypes.ShortDate },
        constructionPeriodIncludes: void 0,
        // Completed date is not editable, but it is marked as readonly on UI.
        // This is necessary because otherwise Angular cannot handle dirty flag of parent FormGroup correctly
        completedDate: { enabled: isNotNotProceedingOrFinalized, type: DateTimeTypes.ShortDate },
        finalClaimDate: { enabled: false, type: DateTimeTypes.ShortDate },
        notProceedingDate: {
          enabled: !info.isFinalized && info.isNotProceeding,
          type: DateTimeTypes.ShortDate,
        },
        isNotProceeding: { enabled: info.isDrawAmountsPaid && !info.isFinalized },
        notProceedingReason: {
          required: isNotProceedingReasonRequired,
          visible: info.isNotProceeding,
          enabled: !info.isFinalized && info.isNotProceeding,
          validators: isNotProceedingReasonRequired ? [requiredNotProceedingReason] : [],
        },
        notProceedingReasonOther: {
          required: isNotProceedingReasonOther,
          visible: isNotProceedingReasonOther,
          enabled: !info.isFinalized && isNotProceedingReasonOther,
          validators: isNotProceedingReasonOther
            ? [requiredNotProceedingReasonOther, maxLengthNotProceedingReasonOther]
            : [],
        },
        handoverDate: { enabled: isNotNotProceedingOrFinalized, type: DateTimeTypes.ShortDate },
        finalizedDate: { enabled: !this.basicInfo.isFinalized && info.isFinalized, type: DateTimeTypes.ShortDate },
        isFinalized: {
          enabled: whenAllOf(
            !this.basicInfo.isFinalized,
            whenOneOf(
              whenAllOf(
                isNotNil(info.completedDate) && isNotNil(info.handoverDate),
                whenOneOf(
                  negate(whenNzRegion()),
                  !info.jobUseCodeOfComplience,
                  isNotNil(info.cocIssueDate) && info.isCocComplianceIssued),
              ),
              info.isNotProceeding),
            !info.isVariationsExists),
        },
        jobUseCodeOfComplience: void 0,
        scheduleStartDate: { enabled: false },
        scheduleCompleteDate: { enabled: false },
        scheduleSlabDate: { enabled: false },
        version: void 0,
        supervisorId: void 0,
        scheduleDrawRuleTasks: void 0,
        scheduleRootTasks: void 0,
        isDrawAmountsPaid: void 0,
        isNotFullyPaidContract: void 0,
        isVariationsExists: void 0,
        isSupervisorExist: void 0,
      },
    });
  }

  build(basicInfo: JobBasicInfo, originalInfo: JobDatesTabInfo): void {
    this.basicInfo = basicInfo;
    this.originalHandoverDate = originalInfo.handoverDate;
    this.originalAuthToCommenceDate = originalInfo.authToCommenceDate;
    // Read by buildSchema, so set before initSchema.
    this.originalIsNotProceeding = !!originalInfo.isNotProceeding;
    this.originalNotProceedingReason = originalInfo.notProceedingReason;

    this.initSchema(originalInfo);
  }

  setContracturalCompletionInfo(response: ContracturalCompletionResponse): void {
    setControlValue(this.group, 'varsDays', response.variationDays);
    setControlValue(this.group, 'sundaysAndSaturdays', response.sundaysAndSaturdays);
    setControlValue(this.group, 'nonWorkingDays', response.nonWorkingDays);
    setControlValue(this.group, 'contracturalCompletionDate', response.contracturalCompletionDate);
  }

  uncomplete(): void {
    setControlValue(this.group, 'completedDate', void 0);
    markControlAsDirty(this.group, 'completedDate');
    this.refresh();
  }

  complete(): void {
    setControlValue(this.group, 'completedDate', currentDate());
    markControlAsDirty(this.group, 'completedDate');
    this.refresh();
  }

  updateFinanceApprovedDate(): void {
    this.updateAndResetIfNil('financeApprovedDate', 'isFinanceApproved', currentDate());
  }

  updateContractDate(): void {
    this.updateAndResetIfNil('contractDate', 'isContract', currentDate());
  }

  updateCouncilApprovedDate(): void {
    this.updateAndResetIfNil('councilApprovedDate', 'isCouncilApproved', currentDate());
  }

  updateAuthToCommenceDate(): void {
    this.updateAndResetIfNil('authToCommenceDate', 'isAuthToCommence', currentDate());
  }

  resetAuthToCommenceDateToOriginal(): void {
    setControlValue(this.group, 'authToCommenceDate', this.originalAuthToCommenceDate);
    setControlValue(this.group, 'isAuthToCommence', false);
    this.refresh();
  }

  updateCocIssueDate(): void {
    if (!getControlValue(this.group, 'isCocComplianceIssued')) {
      setControlValue(this.group, 'cocIssueDate', void 0);
    }
    setControlValue(this.group, 'isCocGjgIssue', false);
    setControlValue(this.group, 'isCocClientIssue', false);

    this.updateAndResetIfNil('cocIssueDate', 'isCocComplianceIssued', currentDate());
  }

  updateNotProceedingDate(): void {
    this.updateAndResetIfNilAndOverridden('notProceedingDate', 'isNotProceeding', currentDate());
    if (!getControlValue(this.group, 'isNotProceeding')) {
      setControlValue(this.group, 'notProceedingReason', void 0);
      setControlValue(this.group, 'notProceedingReasonOther', void 0);
      this.refresh();
    }
  }

  updateFinalizedDate(): void {
    this.updateAndResetIfNilAndOverridden('finalizedDate', 'isFinalized', currentDate());
  }

  updateContractStartDate(): void {
    this.updateAndResetIfNilAndOverridden(
      'contractStartDate',
      'isContractStartDateOverridden',
      void 0,
      void 0);
  }

  updateCompletedDate(): void {
    this.refresh();
  }

  updateHandoverDate(): void {
    this.refresh();
  }

  setHandoverDate(date?: Date): void {
    setControlValue(this.group, 'handoverDate', date);
    this.refresh();
  }

  restoreOriginalHandoverDate(): void {
    this.setHandoverDate(this.originalHandoverDate);
  }

  setSupervisor(user?: UserListItem): void {
    setControlValue(this.group, 'supervisorId', user ? user.userId : void 0);
  }

  private updateAndResetIfNil(dateField: string, allowedField: string, resetValue: any): void {
    const date = getControlValue(this.group, dateField);
    const allowed = getControlValue(this.group, allowedField);
    if (isNil(date) && allowed) {
      setControlValue(this.group, dateField, resetValue);
    }
    this.refresh();
  }

  private updateAndResetIfNilAndOverridden(dateField: string,
                                           overriddenField: string,
                                           resetValue?: any,
                                           originalValue?: any): void {
    const overridden = getControlValue(this.group, overriddenField);
    if (overridden) {
      // if field is overridden try to reset to the provided value
      this.updateAndResetIfNil(dateField, overriddenField, resetValue);
    } else {
      // if field is not overridden back to original value
      setControlValue(this.group, dateField, originalValue);
      this.refresh();
    }
  }
}
